import { useState } from "react";
import { toast } from "sonner";
import { Input, Label, Textarea } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { DialogoDeAcao } from "@/components/usuario/DialogoDeAcao";

export type Solicitacao = {
  id: string;
  tipo: "EXPORTACAO" | "RESET_DADOS" | "EXCLUSAO";
  status: string;
  user: { id: string; name: string | null; email: string } | null;
};

const qtd = (n: number, palavra: string) => `${n} ${palavra}${n === 1 ? "" : "s"}`;

/**
 * Executar ou recusar um pedido LGPD. Executar mostra o que vai ser apagado e só libera com o
 * e-mail da conta digitado — a última trava contra apagar a conta errada.
 */
export function ExecutarSolicitacao({
  solicitacao,
  modo,
  onFechar,
}: {
  solicitacao: Solicitacao | null;
  modo: "executar" | "recusar";
  onFechar: () => void;
}) {
  const utils = trpc.useUtils();
  const [email, setEmail] = useState("");
  const [motivo, setMotivo] = useState("");
  const previa = trpc.admin.lgpd.previa.useQuery({ id: solicitacao?.id ?? "" }, { enabled: !!solicitacao && modo === "executar" });
  const fechar = () => {
    setEmail("");
    setMotivo("");
    onFechar();
  };
  const atualizar = () => {
    utils.admin.lgpd.invalidate();
    utils.admin.users.invalidate();
    utils.admin.auditoria.list.invalidate();
  };
  const executar = trpc.admin.lgpd.executar.useMutation({
    onSuccess: () => {
      toast.success(solicitacao?.tipo === "EXCLUSAO" ? "Conta excluída." : "Dados resetados.");
      atualizar();
      fechar();
    },
    onError: (e) => toast.error(e.message),
  });
  const recusar = trpc.admin.lgpd.recusar.useMutation({
    onSuccess: () => {
      toast.success("Pedido recusado — o titular recebe um e-mail.");
      atualizar();
      fechar();
    },
    onError: (e) => toast.error(e.message),
  });
  if (!solicitacao) return null;
  const exclusao = solicitacao.tipo === "EXCLUSAO";
  const emailDaConta = solicitacao.user?.email ?? "";

  if (modo === "recusar") {
    return (
      <DialogoDeAcao aberto onFechar={fechar} titulo="Recusar pedido" rotulo="Recusar"
        descricao="O titular recebe um e-mail com o motivo."
        pronto={motivo.trim().length >= 3} ocupado={recusar.isPending} onConfirmar={() => recusar.mutate({ id: solicitacao.id, motivo })}>
        <Label htmlFor="motivo-recusa">Motivo</Label>
        <Textarea id="motivo-recusa" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </DialogoDeAcao>
    );
  }

  return (
    <DialogoDeAcao
      aberto
      onFechar={fechar}
      titulo={exclusao ? "Executar exclusão da conta" : "Executar reset dos dados"}
      rotulo={exclusao ? "Excluir conta" : "Resetar dados"}
      perigosa
      pronto={email.trim().toLowerCase() === emailDaConta.toLowerCase()}
      ocupado={executar.isPending}
      onConfirmar={() => executar.mutate({ id: solicitacao.id, emailDigitado: email })}
      descricao={
        exclusao
          ? "A conta é apagada. As organizações dela são apagadas — ou encerradas, se um laboratório ainda tiver pedidos dela (ficam com o paciente anonimizado). Não dá pra desfazer."
          : "Pacientes, consultas, pedidos, arquivos e estoque das organizações dela são apagados; conta, organizações, equipe e configurações ficam. Não dá pra desfazer."
      }
    >
      <ul className="space-y-1 rounded-md bg-zinc-50 p-2.5 text-xs text-zinc-700">
        {previa.isLoading && <li>Calculando…</li>}
        {(previa.data ?? []).map((o) => (
          <li key={o.id}>
            <b>{o.name}</b>: {qtd(o.pacientes, "paciente")} · {qtd(o.consultas, "consulta")} · {qtd(o.pedidos, "pedido")} · {qtd(o.arquivos, "arquivo")}
            {o.outrosMembros > 0 ? ` · ${o.outrosMembros} outro(s) na equipe` : ""}
          </li>
        ))}
        {previa.data?.length === 0 && <li>Nenhuma organização de que seja dona.</li>}
      </ul>
      <Label htmlFor="email-confirmacao">Digite o e-mail da conta para confirmar ({emailDaConta})</Label>
      <Input id="email-confirmacao" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
    </DialogoDeAcao>
  );
}
